import { SortDirection, GetJobsSortKey } from "generated/graphql";
import { usePaginatedVariables } from "hooks/strings/usePaginatedVariables";
import usePaginatedResources from "hooks/usePaginatedResources";
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import MDBox from "components/MDBox";
import { Grid, IconButton, InputBase, Paper, Skeleton } from "@mui/material";
import MDSelect from "components/MDSelect/MDSelect";
import useGetAllCompanies from "hooks/companies/useGetAllCompanies";
import useGetAllOrganizationUsers from "hooks/users/useGetAllOrganizationUsers";
import SearchIcon from "@mui/icons-material/Search";
import useGetAllCrews from "hooks/crews/useGetAllCrews";
import MDTypography from "components/MDTypography";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";
import localizedFormat from "dayjs/plugin/localizedFormat";
import MDButton from "components/MDButton";
import useGetJobPhases from "./job-phases/useGetJobPhases";

dayjs.extend(utc);
dayjs.extend(localizedFormat);

export const jobPhasesSortKeyMap = {
  id: GetJobsSortKey.ID,
  company: GetJobsSortKey.COMPANY,
  start: GetJobsSortKey.START,
  status: GetJobsSortKey.STATUS,
  default: GetJobsSortKey.START,
};

export const jobPhasesInitialSortDirectionKey = "desc";
export const jobPhasesInitialSortDirectionValue = SortDirection.DESC;
export const jobPhasesInitialSortKey = "createdAt";
export const jobPhasesInitialSortKeyValue = GetJobsSortKey.START;

const Search = ({ onQueryChange, onChangeStartDate, startDate }) => {
  const ref = useRef(null);
  return (
    <MDBox position="relative" zIndex={2}>
      <MDBox
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        flexWrap="wrap"
        gap={1.5}
      >
        <MDBox
          display="flex"
          alignItems="center"
          flexWrap="wrap"
          gap={1}
          sx={{
            ".date-input": {
              border: "1px solid #d7dbe3",
              borderRadius: "10px",
              padding: "8px 12px",
              minHeight: "40px",
              fontSize: "13px",
              color: "#344767",
              fontFamily: "Roboto",
              outline: "none !important",
              backgroundColor: "#fff",
            },
          }}
        >
          <input
            type="date"
            step="1"
            onChange={(e) => onChangeStartDate(e.target.value)}
            defaultValue={dayjs(startDate).format("YYYY-MM-DD")}
            className="date-input"
            ref={ref}
          />
          <MDButton
            color="blue"
            size="small"
            sx={{ minWidth: 0, px: 1.5, py: 0.75 }}
            onClick={() => {
              onChangeStartDate("");
              ref.current.value = "";
            }}
          >
            Clear Date
          </MDButton>
        </MDBox>
        <Paper
          component="form"
          elevation={0}
          sx={{
            p: "2px 6px",
            display: "flex",
            alignItems: "center",
            border: "1px solid #d7dbe3",
            borderRadius: "12px",
            minHeight: "40px",
            minWidth: { xs: "100%", md: 280 },
            backgroundColor: "#fff",
          }}
          onSubmit={(e) => {
            e.preventDefault();
          }}
        >
          <InputBase
            sx={{ ml: 1, flex: 1, fontSize: "13px" }}
            placeholder="Search Job Phase"
            inputProps={{ "aria-label": "search job phase" }}
            onChange={({ currentTarget }: any) => {
              onQueryChange(currentTarget.value);
            }}
          />
          <IconButton type="button" sx={{ p: "10px" }} aria-label="search">
            <SearchIcon />
          </IconButton>
        </Paper>
      </MDBox>
    </MDBox>
  );
};

const Filters = ({
  onCrewIdChange,
  selectedCrew,
  crews,
  foreManOptions,
  onForemanUserIdChange,
  selectedForeman,
  crewsLoading,
  jobCostingStatus,
  onJobCostingStatusChange,
}) => {
  return (
    <MDBox position="relative" zIndex={2}>
      {crewsLoading ? (
        <MDTypography>
          <Skeleton />
        </MDTypography>
      ) : (
        <Grid
          container
          spacing={1.5}
          sx={{
            "& .MuiFormControl-root": {
              minHeight: "40px",
            },
            "& .MuiInputBase-root": {
              minHeight: "40px",
              backgroundColor: "#fff",
            },
            "& .MuiInputLabel-root": {
              fontSize: "13px",
            },
            "& .MuiSelect-select": {
              fontSize: "13px",
            },
          }}
        >
          <Grid item xs={12} sm={6} lg={4}>
            <MDSelect
              fullWidth
              label="Select Foreman"
              name="areaProduct"
              options={[{ label: "View All", value: "" }, ...foreManOptions]}
              onChange={(e) => {
                onForemanUserIdChange(e.target.value);
              }}
              value={selectedForeman}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <MDSelect
              fullWidth
              label="Select Crew"
              name="areaProduct"
              options={[{ label: "View All", value: "" }, ...crews]}
              onChange={(e) => {
                onCrewIdChange(e.target.value);
              }}
              value={selectedCrew}
            />
          </Grid>
          <Grid item xs={12} sm={6} lg={4}>
            <MDSelect
              fullWidth
              label="Costing Status"
              name="jobCostingStatus"
              options={[
                { label: "View All", value: "" },
                { label: "Not Started", value: "NOT_STARTED" },
                { label: "In Progress", value: "IN_PROGRESS" },
                { label: "Within Threshold", value: "WITHIN_THRESHOLD" },
                { label: "Out of Threshold", value: "OUT_OF_THRESHOLD" },
              ]}
              onChange={(e) => {
                onJobCostingStatusChange(e.target.value);
              }}
              value={jobCostingStatus}
            />
          </Grid>
        </Grid>
      )}
    </MDBox>
  );
};

export default function useJobPhasesTableData() {
  let savedFilters = JSON.parse(localStorage.getItem("useJobPhasesTableFilters")) ?? null;
  const [jobCostingStatus, setJobCostingStatus] = useState<string>("");
  const { filtering, sorting, offset } = usePaginatedVariables({
    initialSortDirection: jobPhasesInitialSortDirectionKey,
    initialSortKey: jobPhasesInitialSortKey,
    initialCompanyId: savedFilters?.companyId,
    initialBillingStatuses: savedFilters?.billingStatuses,
  });

  useEffect(() => {
    localStorage.setItem("useJobPhasesTableFilters", JSON.stringify(filtering));
  }, [filtering]);

  const {
    loading: companiesLoading,
    data: companiesData,
    error: companiesError,
  } = useGetAllCompanies(true);

  const {
    loading: organizationUsersLoading,
    data: organizationUsersData,
    error: organizationUsersError,
  } = useGetAllOrganizationUsers();

  const { loading: crewsLoading, data: crewsData } = useGetAllCrews();

  const crewsOptions = crewsData?.map((crew) => {
    const crewSelectOptions = { label: crew?.name, value: crew?.id };
    return crewSelectOptions;
  });

  const ids = crewsData?.map(({ foremanUser }) => foremanUser?.id);

  const foremanOptions = crewsData
    ?.filter(({ foremanUser }, index) => !ids.includes(foremanUser?.id, index + 1))
    .map((crew) => {
      return { label: crew?.foremanUser?.name, value: crew?.foremanUser?.id };
    });

  useEffect(() => {
    filtering.setStartsAt(dayjs(Date.now()).format("YYYY-MM-DD"));
  }, []);

  const { jobPhasesData, paginationData, queryLoading, queryError } = useGetJobPhases({
    filtering,
    sorting,
    offset,
  });

  const filteredJobPhasesData = useMemo(() => {
    if (!jobCostingStatus) {
      return jobPhasesData;
    }

    return jobPhasesData?.filter(
      (jobPhase) => jobPhase?.jobCostingCompletionStatus === jobCostingStatus
    );
  }, [jobCostingStatus, jobPhasesData]);

  const pagination = usePaginatedResources({
    paginate: offset.paginate,
    paginatorInfo: paginationData,
  });

  const columns = useMemo(() => {}, []);

  const serachBox = (
    <Search
      onQueryChange={filtering.setSearchTerm}
      onChangeStartDate={filtering.setStartsAt}
      startDate={filtering.startsAt}
    />
  );

  const filterControl = (
    <Filters
      onCrewIdChange={filtering.setCrewID}
      selectedCrew={filtering.crewID}
      onForemanUserIdChange={filtering.setForemanUserId}
      selectedForeman={filtering.foremanUserId}
      crewsLoading={crewsLoading}
      crews={crewsOptions}
      foreManOptions={foremanOptions}
      jobCostingStatus={jobCostingStatus}
      onJobCostingStatusChange={setJobCostingStatus}
    />
  );

  const renderRowSubComponent = (row: any) => <pre>{JSON.stringify(row, undefined, 2)}</pre>;

  return {
    queryLoading,
    queryError,
    columns,
    filterControl,
    renderRowSubComponent,
    pagination,
    filtering,
    sorting,
    paginatorInfo: pagination.paginatorInfo,
    companiesData,
    companiesLoading,
    companiesError,
    organizationUsersLoading,
    organizationUsersData,
    organizationUsersError,
    jobPhasesData: filteredJobPhasesData,
    serachBox,
  } as const;
}
